import { FC, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import moment from "moment";
import { ChatAgent, ChatMessage } from "../../../interfaces/Chat/Chat";
import { useChatStore } from "../../../stores/ChatStore";
import { displayDate } from "../../../functions/chat";
import { ChatMessageBubble } from "../ChatMessage/ChatMessageBubble";
import { formatSecondsToTime, getDateRangeDuration } from "../../../functions/general";

interface DetailSessionProps {
    dataSession: ChatAgent
    setShowDetailSession: React.Dispatch<React.SetStateAction<boolean>>
}

const ChatDetailSession: FC<DetailSessionProps> = ({ dataSession, setShowDetailSession }) => {
    const { phoneNumber, listChatMessages } = useChatStore()
    const intl = useIntl()
    const [listChat, setListChat] = useState<ChatMessage[]>([])
    const containerRef = useRef<HTMLDivElement>(null);

    function filterChatsInSessionRange(chats: ChatMessage[], session: ChatAgent): ChatMessage[] {
        const startDate = moment(session.started.date);
        const endDate = session.ended ? moment(session.ended.date) : moment();

        return chats.filter(chat => {
            const receivedAt = moment(chat.receivedAt, "YYYY-MM-DD HH:mm:ss");
            return receivedAt.isBetween(startDate, endDate, undefined, '[]');
        });
    }

    const scrollToChat = (index: number) => {
        const target = document.getElementById(`message-session-${index}`);
        if (target && containerRef.current) {
            target.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "nearest",
            });
        } else if (containerRef.current) {
            containerRef.current.scrollTo({
                top: containerRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    };

    useEffect(() => {
        const allChats = listChatMessages[phoneNumber] || [];
        const filteredChats = filterChatsInSessionRange(allChats, dataSession);

        // Hapus duplikat berdasarkan message.id
        const uniqueChats = Array.from(
            new Map(filteredChats.map(chat => [chat.id, chat])).values()
        );

        setListChat(uniqueChats);
    }, [listChatMessages, phoneNumber, dataSession]);

    return (
        <div style={{ minHeight: "calc(100vh - 1rem)" }}>
            <div className="card mb-5">
                <h3 className="py-3 px-5 d-flex align-items-center gap-3 mb-0">
                    <i className="fas fa-times fs-3 text-hover-primary cursor-pointer" onClick={() => setShowDetailSession(false)}></i>
                    Detail {dataSession.handler.name}
                </h3>
                <div className="card-body">
                    <div className="d-flex align-items-center flex-wrap flex-grow-1 mb-5">
                        <div className="d-flex flex-column flex-grow-1 my-lg-0 my-2 pe-3">
                            <span className="fs-6 fw-bolder mb-1">{intl.formatMessage({ id: "FORM.LABEL.SESSION_START" })}</span>
                            <span className="text-gray-400 fw-bold fs-7">{intl.formatMessage({ id: "FORM.LABEL.AGENT" })} : {dataSession.started.name}</span>
                            <span className="text-gray-400 fw-bold fs-7">{intl.formatMessage({ id: "FORM.LABEL.DATE" }, { title: '' })} : {moment(dataSession.started.date).utc(true).format("DD MMMM YYYY HH:mm")}</span>
                        </div>
                    </div>
                    {
                        dataSession.ended !== null &&
                        <div className="d-flex align-items-center flex-wrap flex-grow-1 mb-5">
                            <div className="d-flex flex-column flex-grow-1 my-lg-0 my-2 pe-3">
                                <span className="fs-6 fw-bolder mb-1">{intl.formatMessage({ id: "FORM.LABEL.SESSION_END" })}</span>
                                <span className="text-gray-400 fw-bold fs-7">{intl.formatMessage({ id: "FORM.LABEL.AGENT" })} : {dataSession.ended.name}</span>
                                <span className="text-gray-400 fw-bold fs-7">{intl.formatMessage({ id: "FORM.LABEL.DATE" }, { title: '' })} : {moment(dataSession.ended.date).utc(true).format("DD MMMM YYYY HH:mm")}</span>
                                <span className="text-gray-400 fw-bold fs-7">{intl.formatMessage({ id: "FORM.LABEL.DURATION" })} : {dataSession.ended ? getDateRangeDuration(dataSession.started.date, dataSession.ended.date) : "-"}</span>
                                <span className="text-gray-400 fw-bold fs-7">{intl.formatMessage({ id: "FORM.LABEL.AVERAGE_RESPONSE_TIME" })} : {formatSecondsToTime(dataSession.avgReplySpeedInSeconds)}</span>
                                <span className="text-gray-400 fw-bold fs-7">{intl.formatMessage({ id: "FORM.LABEL.NOTE" })} : {dataSession.ended.note ? dataSession.ended.note : "-"}</span>
                            </div>
                        </div>
                    }
                </div>
            </div>
            <div className="card">
                <div className="card-body">
                    <div className="fs-4 text-gray-800 fw-bolder mb-3">
                        Chat
                    </div>
                    <div
                        ref={containerRef}
                        style={{
                            height: "100%",
                            overflowY: "auto",
                            position: "relative",
                        }}
                    >
                        {listChat.map((chat, index) => {
                            const message = listChat[index];
                            const prevMessage = listChat[index - 1];

                            const showDate =
                                index === 0 ||
                                moment(message.receivedAt).format("DD MMMM YYYY") !==
                                moment(prevMessage?.receivedAt).format("DD MMMM YYYY");


                            return (
                                <div
                                    key={index}
                                    id={`message-session-${index}`}
                                    className={`message-item`}
                                >
                                    {showDate && (
                                        <div className="d-flex justify-content-center">
                                            <span className="badge badge-light-primary fs-7 fw-bold mb-2">
                                                {displayDate(message, intl)}
                                            </span>
                                        </div>
                                    )}

                                    <div className="position-relative">
                                        <ChatMessageBubble
                                            message={message}
                                            key={message.id}
                                            scrollToChat={scrollToChat}
                                            isAction={false}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
}

export { ChatDetailSession }